import * as React from "react";
import { Divider, Fade, Theme, Typography } from "@mui/material";
import {
  addressLookup,
  createUrlSafePathing,
  fileNameFormat,
  formatDate,
  toTitleCase,
} from "../../common/services/Utilities";
import {
  KeyStringValue,
  KeyValue,
  OscrLevelTwoRecord,
  Query,
  SearchPillData,
  SimpleTableRow,
  SortDirection,
  SortProperties,
  TableHeader,
} from "../../interfaces";
import {
  BRANCH_OFFICE_RELOCATIONS,
  branchOfficeRelocation,
  BUSINESS_COMBINATIONS,
  BUSINESS_COMBINATIONS_FAILURES,
  businessCombinationFailures,
  businessCombinations,
  CHANGECODE_DESC,
  CONVERSIONS,
  conversions,
  FORMATTED_DATE,
  FROM_ADDRESS,
  FROM_OFFICE_ADDRESS,
  INTERIM_MERGERS,
  interimMergers,
  levelTwoOrderedKeysLookup,
  LIQUIDATIONS,
  liquidations,
  MAIN_OFFICE_RELOCATIONS,
  mainOfficeRelocations,
  NEW_INSTITUTIONS,
  newInstitutions,
  OFF_SERVTYPE_DESC,
  OFFICE_ADDRESS,
  OFFICE_CLOSINGS,
  OFFICE_OPENINGS,
  officeClosingHeaders,
  officeOpeningsHeaders,
  PANDAS,
  PHYSICAL_ADDRESS,
  purchaseAssumptionBranches,
  structureChangeLevelTwoPathingMap,
  TITLE_CHANGES,
  titleChanges,
  TRUST_POWERS,
  trustPowers,
  VIEW_DETAILS,
  SUR_CHANGECODE_DESC,
  defaultStructureChangeLevelTwoSortFields,
} from "../../refdata/constants";
import SearchPills from "../../containers/SearchPills";
import ResultPagination from "../../containers/ResultPagination";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import ModalDialog from "../ModalDialog";
import { AppUrl } from "../../config";
import AddressDisplay from "../AddressDisplay";
import InfoIcon from "@mui/icons-material/Info";
import SimpleTableBody from "../MultiHeaderTable/SimpleTableBody";
import makeStyles from "@mui/styles/makeStyles";
import { useParams, useHistory } from "react-router-dom";
import ViewInstitutionModalSubtitle from "../ViewInstitutionModalSubtitle";
import StructureChangeModalDialog from "./StructureChangeModalDialog";
import ApiDataDownload from "../ApiDataDownload";
import { getHistoryAggregationApiCalls } from "../../common/services/HistoryUtilities";
import { fetchData } from "../../common/services/ApiClient";
import { eventCode } from "../../refdata/FilterOptions";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import { ThemeProvider } from "@mui/styles";
import PFATheme from "../../PFATheme";
import ShowHideButton from "../ShowHideButton";

interface Props {
  appState: Query;
  oscrSearchPillData: SearchPillData[];
  sortProperties: SortProperties;
  onResultSuccess: () => void;
  onResultFailure: (error: Error) => void;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
}

const useClasses = makeStyles((theme: Theme) => ({
  branchesButton: {
    "height": 40,
    "width": 160,

    "&.MuiButton-root": {
      margin: "10px auto",
      textAlign: "center",
      display: "block",
    },
  },
  modalSubtitle: {
    paddingBottom: 10,
  },
  PANDAFootnote: {
    color: "rgb(102, 102, 102)",
    maxWidth: 432,
    marginLeft: 5,
    paddingLeft: 8,
    paddingBottom: 8,
    fontSize: 13,
    lineHeight: "15px",
    marginTop: -24,
  },
}));

export function OSCRAddressDownload(
  data: OscrLevelTwoRecord,
  addressType: string,
) {
  const addressObject = addressLookup(data)[addressType];

  return addressObject.address &&
    addressObject.city &&
    addressObject.state &&
    addressObject.zip
    ? `${addressObject.address}, ${addressObject.city}, ${addressObject.state} ${addressObject.zip}`
    : "";
}

export function generateViewInstitutionsHref(
  oscrRecord: OscrLevelTwoRecord,
  eventType: string,
) {
  const cert: { [key: string]: string } = {
    [BUSINESS_COMBINATIONS_FAILURES]: "SUR_CERT",
    [BUSINESS_COMBINATIONS]: "SUR_CERT",
    [PANDAS]: "ACQ_CERT",
  };
  return `${AppUrl("BankFind")}/details/${
    cert[eventType] ? oscrRecord[cert[eventType]] : oscrRecord.CERT
  }`;
}

export function formattedValues(
  key: string,
  levelTwoRecord: OscrLevelTwoRecord,
  download?: boolean,
) {
  switch (key) {
    case PHYSICAL_ADDRESS:
    case OFFICE_ADDRESS:
    case FROM_ADDRESS:
    case FROM_OFFICE_ADDRESS:
      return download ? (
        OSCRAddressDownload(levelTwoRecord, key)
      ) : (
        <AddressDisplay addressObject={addressLookup(levelTwoRecord)[key]} />
      );
    case FORMATTED_DATE:
      return formatDate(levelTwoRecord.EFFDATE);
    case OFF_SERVTYPE_DESC:
      return toTitleCase(levelTwoRecord.OFF_SERVTYPE_DESC);
    case CHANGECODE_DESC:
      return `${toTitleCase(levelTwoRecord.CHANGECODE_DESC)}${
        levelTwoRecord.CHANGECODE === 224 &&
        levelTwoRecord.CHANGECODE_DESC.slice(-1) !== ")"
          ? ")"
          : ""
      }`;
    case SUR_CHANGECODE_DESC:
      return toTitleCase(levelTwoRecord.SUR_CHANGECODE_DESC);
    case VIEW_DETAILS:
      return <InfoIcon color="primary" />;
    default:
      return levelTwoRecord[key] as string | number | React.ReactElement;
  }
}

export function getLevelThreeTitle(
  eventType: string,
  oscrLevelThreeDetails: OscrLevelTwoRecord,
) {
  const titleLookup: KeyValue = {
    [BUSINESS_COMBINATIONS]: oscrLevelThreeDetails.SUR_INSTNAME,
    [BUSINESS_COMBINATIONS_FAILURES]: oscrLevelThreeDetails.SUR_INSTNAME,
    [PANDAS]: oscrLevelThreeDetails.ACQ_INSTNAME,
  };

  return titleLookup[eventType]
    ? titleLookup[eventType]
    : oscrLevelThreeDetails.INSTNAME;
}

export const modalSubtitle = (
  oscrRecord: OscrLevelTwoRecord,
  eventType: string,
  classes: KeyStringValue,
  oscrLevelFour: KeyValue,
  onclick: () => void,
) => {
  const showViewInstitution =
    oscrRecord.CLASS && !["NC", "NS", "CU"].includes(oscrRecord.CLASS);

  const branchOfficeCount = oscrRecord.ACQ_BRANCHES
    ? `${oscrRecord.ACQ_BRANCHES.toLocaleString()} ${
        oscrRecord.ACQ_BRANCHES === 1 ? "Branch Office" : "Branch Offices"
      }`
    : "";
  return (
    <div className={showViewInstitution ? classes.modalSubtitle : undefined}>
      {eventType}{" "}
      <Fade
        in={oscrLevelFour.resultData && oscrLevelFour.resultData.length > 0}
      >
        <Typography color="primary" style={{ display: "inline-block" }}>
          - {branchOfficeCount}
        </Typography>
      </Fade>
      {showViewInstitution ? (
        <ThemeProvider theme={PFATheme}>
          <ViewInstitutionModalSubtitle onClick={onclick} />
        </ThemeProvider>
      ) : (
        undefined
      )}
    </div>
  );
};

export function showViewBranchesButton(eventType: string) {
  const viewableEventTypes = [
    "Business Combinations",
    "Business Combinations - Failures",
    "Branch Purchases & Assumptions by Institution",
    "Liquidations",
  ];
  return viewableEventTypes.includes(eventType);
}

export function tableHeaderValues(eventType: string) {
  const header: { [key: string]: TableHeader[][] } = {
    [NEW_INSTITUTIONS]: newInstitutions,
    [BUSINESS_COMBINATIONS]: businessCombinations,
    [BUSINESS_COMBINATIONS_FAILURES]: businessCombinationFailures,
    [INTERIM_MERGERS]: interimMergers,
    [CONVERSIONS]: conversions,
    [TITLE_CHANGES]: titleChanges,
    [MAIN_OFFICE_RELOCATIONS]: mainOfficeRelocations,
    [LIQUIDATIONS]: liquidations,
    [TRUST_POWERS]: trustPowers,
    [BRANCH_OFFICE_RELOCATIONS]: branchOfficeRelocation,
    [PANDAS]: purchaseAssumptionBranches,
    [OFFICE_OPENINGS]: officeOpeningsHeaders,
    [OFFICE_CLOSINGS]: officeClosingHeaders,
  };

  return header[eventType];
}

export function transformLevelTwoData(
  levelTwoResponse: OscrLevelTwoRecord[],
  eventType: string,
  setLevelThree: (val: OscrLevelTwoRecord) => void,
  setDisplay: (val: boolean) => void,
  download?: boolean,
): SimpleTableRow[] {
  return levelTwoResponse.map((levelTwoRecord: OscrLevelTwoRecord) => {
    const tableCellData: SimpleTableRow = {};
    levelTwoOrderedKeysLookup[eventType].forEach((key: string) => {
      tableCellData[key] = {
        value: download
          ? formattedValues(key, levelTwoRecord, true)
          : formattedValues(key, levelTwoRecord),
        styles: {},
      };
    });

    tableCellData.rowOnClick = (e: any) => {
      if (e.key === "Enter" || e.nativeEvent.type === "click") {
        setLevelThree(levelTwoRecord);
        setDisplay(true);
      }
    };
    return tableCellData;
  });
}

export default function StructureChangeLevelTwo({
  appState,
  oscrSearchPillData,
  sortProperties,
  updateSort,
}: Props) {
  const classes = useClasses();
  const [oscrLevelFour, setOscrLevelFour] = React.useState({
    resultData: [],
    dataLoaded: false,
    timeout: 0,
  });
  const [displayDialog, setDisplayDialog] = React.useState(false);
  const [oscrLevelThreeDetails, setOscrLevelThreeDetails] = React.useState<
    OscrLevelTwoRecord
  >({
    TRANSNUM: 0,
    CHANGECODE: 0,
    REPORT_TYPE: 0,
    CHANGECODE_DESC: "",
    SUR_CHANGECODE_DESC: "",
    PROCDATE: "",
    EFFDATE: "",
    ENDDATE: "",
    UNINUM: 0,
    ORG_ROLE_CDE: 0,
    INSTNAME: "",
    SUR_INSTNAME: "",
    OFF_SERVTYPE_DESC: "",
  });
  const [levelTwoState, setLevelTwoState] = React.useState({
    createTimestamp: "",
    recordCount: 0,
    resultData: [],
  });

  let { eventType } = useParams();
  eventType = structureChangeLevelTwoPathingMap[eventType.split("?")[0]];
  const history = useHistory();

  const showHideSearchPills = (
    <>
      <Divider />
      <ThemeProvider theme={PFATheme}>
        <ShowHideButton
          title="Selected Search Criteria"
          body={<SearchPills dataArray={oscrSearchPillData} />}
        />
      </ThemeProvider>
    </>
  );

  const subTitle = modalSubtitle(
    oscrLevelThreeDetails,
    eventType,
    classes,
    oscrLevelFour,
    () =>
      history.push({
        pathname: generateViewInstitutionsHref(
          oscrLevelThreeDetails,
          eventType,
        ),
        search: "?bankfindLevelThreeView=History",
        state: {
          url: `${AppUrl("OSCR")}/${createUrlSafePathing(eventType)}${
            window.location.search
          }`,
          locationCity: appState.search.city,
          locationState: appState.search.stateSearch,
          fromOSCR: true,
        },
      }),
  );

  const transformedData = transformLevelTwoData(
    levelTwoState.resultData,
    eventType,
    setOscrLevelThreeDetails,
    setDisplayDialog,
  );
  React.useEffect(() => {
    const arrayOfTitles = eventCode.map(event => event.label);
    const currentEventType =
      structureChangeLevelTwoPathingMap[
        window.location.pathname.split("/").slice(-1)[0]
      ];
    // The second check is due to the delay in change from the route from using
    // the browser back button so we want to ensure that the path is correct before making the api calls as it gets rerendered still during the change in paths
    if (eventType && arrayOfTitles.includes(currentEventType)) {
      let isMounted = true;
      const historyApiArray = getHistoryAggregationApiCalls(
        appState,
        false,
        eventType,
      );
      (async () => {
        await Promise.all(
          historyApiArray.map((path: string) => fetchData(path)),
        )
          .then(response => {
            if (isMounted) {
              const responseData = [].concat(
                ...response.map(res => res.data as never[]),
              );
              const recordCount = response.reduce(
                (count, data) => count + data.totals.count,
                0,
              );
              setLevelTwoState({
                resultData: responseData,
                recordCount,
                createTimestamp: response[0].createTimestamp,
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [appState, eventType]);

  React.useEffect(() => {
    if (!appState.sort.sortField) {
      updateSort(defaultStructureChangeLevelTwoSortFields[eventType], "ASC");
    }
  }, [appState.sort.sortField, eventType, updateSort]);

  useInitialPageLoad("oscrDetails");
  return (
    <ThemeProvider theme={PFATheme}>
      <Typography component="div" id="multi-header-table-title">
        <>
          <Typography
            variant="h2"
            id="resultSummary"
            style={{
              position: "relative",
            }}
          >
            {`${eventType} - ${levelTwoState.recordCount.toLocaleString()} ${
              levelTwoState.recordCount === 1
                ? "Matching Result"
                : "Matching Results"
            }`}
            <ApiDataDownload
              apiPathLink="history"
              app="structureChange"
              numberOfResults={levelTwoState.recordCount}
              apiData={[
                {
                  appState,
                  fileName: `${fileNameFormat(eventType)}.csv`,
                  apiPath: "history",
                },
              ]}
            />
          </Typography>
          {showHideSearchPills}
        </>
      </Typography>

      <ResultPagination count={levelTwoState.recordCount}>
        <MultiHeaderTable
          sortProperties={sortProperties}
          tableHeaders={tableHeaderValues(eventType)}
          tableBody={<SimpleTableBody tableData={transformedData} />}
        />
      </ResultPagination>

      <ModalDialog
        title={getLevelThreeTitle(eventType, oscrLevelThreeDetails)}
        subtitle={subTitle}
        display={displayDialog}
        setDisplay={setDisplayDialog}
        onExited={() => {
          setOscrLevelFour({
            resultData: [],
            dataLoaded: false,
            timeout: 0,
          });
        }}
      >
        <StructureChangeModalDialog
          dataLoaded={oscrLevelFour.dataLoaded}
          timeout={oscrLevelFour.timeout}
          showViewBranches={showViewBranchesButton(eventType)}
          eventType={eventType}
          data={oscrLevelThreeDetails}
          viewBranchesData={oscrLevelFour.resultData}
          setViewBranchesData={setOscrLevelFour}
        />
        {eventType === PANDAS && !oscrLevelThreeDetails.OUT_CERT ? (
          <Typography className={classes.PANDAFootnote} id={"PANDA_footnote"}>
            *Please use the “Contact the FDIC” button for additional details
            pertaining to this event.
          </Typography>
        ) : (
          ""
        )}
      </ModalDialog>
    </ThemeProvider>
  );
}
